'use client'

import { FC, useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import cn from 'classnames'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { useGetTranslations } from '@/root/shared-hooks/'

interface ReservationTimerProps {
  expireAt: string
}

export const ReservationTimer: FC<ReservationTimerProps> = ({ expireAt }) => {
  const { getTranslation } = useGetTranslations()
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [isExpiringSoon, setIsExpiringSoon] = useState(false)
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = DateTime.now()
      const expiry = DateTime.fromISO(expireAt, { zone: 'utc' }).toLocal()
      const diff = expiry.diff(now, ['minutes', 'seconds'])

      if (diff.minutes < 0) {
        setIsExpired(true)
        setIsExpiringSoon(false)
        return
      }

      const totalMinutes = Math.floor(diff.minutes)
      const seconds = Math.floor(diff.seconds % 60)

      setIsExpiringSoon(totalMinutes < 5)
      setTimeRemaining(`${totalMinutes}m ${seconds}s`)
    }

    updateTimeRemaining()

    const interval = setInterval(updateTimeRemaining, 1000)

    return () => clearInterval(interval)
  }, [expireAt])

  if (isExpired) {
    return <Banner variant="warning" body={getTranslation('sessionExpired')} />
  }

  return (
    <div
      className={cn('flex items-center justify-between rounded-lg border px-4 py-2 text-sm', {
        'border-warning-base bg-warning-lightest text-warning-dark': isExpiringSoon,
        'border-primary-lightest bg-primary-lightest text-primary-base': !isExpiringSoon,
      })}
    >
      <span>{getTranslation('reservationExpiresIn')}</span>
      <span className="font-semibold tabular-nums">{timeRemaining}</span>
    </div>
  )
}
